/**
 * SSOT: unpaid guaranteed hotel bookings whose free-cancellation window
 * is within the next N calendar days (local timezone, typically Israel).
 *
 * If the agent does not pay before refundable-until, the hold drops and
 * cannot be recovered.
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { parseCancellationDeadline } from "../dynamicPackage/hasFutureCancellationPolicy";
import { parseHotelCancellationDate } from "../formatHotelCancellationDate";
import { ordersDashboardCategoryPath } from "../ordersDashboardRoutes";

dayjs.extend(customParseFormat);

export const HOTEL_CXL_DROP_RISK_WINDOW_DAYS = 15;
export const HOTEL_CXL_DROP_RISK_URL_PARAM = "cxlRisk";

export const HOTEL_CXL_DROP_RISK_SEVERITY = Object.freeze({
  warning: "warning",
  urgent: "urgent",
  critical: "critical",
});

const CONFIRMED_HOTEL_STATUSES = new Set([
  "OK",
  "CONFIRMED",
  "RESERVED",
  "BOOKED",
]);

function isTrueLike(value) {
  return value === true || value === 1 || String(value).trim().toLowerCase() === "true";
}

function strip(value) {
  return value == null ? "" : String(value).trim();
}

function isHotelPaid(order) {
  return isTrueLike(order?.paid ?? order?.elig_hp_paid);
}

function isHotelCancelled(order) {
  if (isTrueLike(order?.cancelled)) return true;
  const status = strip(order?.status);
  return status === "בוטלה" || status.toLowerCase() === "cancelled";
}

function isHotelOffer(order) {
  return isTrueLike(order?.is_offer);
}

function hasBookingRef(order) {
  const ref = strip(order?.booking_ref ?? order?.bookingRef);
  return Boolean(ref) && ref !== "-" && ref !== "-1" && ref.toLowerCase() !== "none";
}

/**
 * Guaranteed / held hotel (משוריין) — supplier confirmed or has a booking ref.
 * Excludes offers and captured-payment-pending-booking shells (orderid -1 + PENDING).
 */
export function isGuaranteedHotelHold(order) {
  if (!order || typeof order !== "object") return false;
  if (isHotelOffer(order)) return false;
  if (hasBookingRef(order)) return true;
  const status = strip(
    order.orderstatus ?? order.booking_status ?? order.supplier_booking_status
  ).toUpperCase();
  if (CONFIRMED_HOTEL_STATUSES.has(status)) return true;
  const orderid = strip(order.orderid ?? order.elig_hp_orderid);
  if (!orderid || orderid === "-1") return false;
  if (!status || status === "PENDING") return false;
  return true;
}

export function pickHotelRefundableUntilRaw(order) {
  if (!order || typeof order !== "object") return null;
  const candidates = [
    order.refundableuntil,
    order.refundableUntil,
    order.refundable_until,
    order.selectedHotelPackage?.refundableUntil,
  ];
  for (const raw of candidates) {
    if (raw == null) continue;
    const s = typeof raw === "string" ? raw.trim() : raw;
    if (s === "" || String(s).toLowerCase() === "none") continue;
    return s;
  }
  return null;
}

/**
 * Calendar day of the customer-facing free-cancel date (DD/MM/YYYY),
 * matching "ביטול ללא עלות עד …". ISO datetimes use the date part only
 * so timezone midnight does not shift the countdown by a day.
 */
export function parseHotelFreeCancelCalendarDay(order) {
  const raw = pickHotelRefundableUntilRaw(order);
  if (raw == null) return null;
  const { parsed } = parseHotelCancellationDate(raw);
  if (parsed?.isValid()) return parsed.startOf("day");
  const fallback = parseCancellationDeadline(raw);
  if (fallback?.isValid()) return fallback.startOf("day");
  return null;
}

function calendarDaysUntil(deadlineDay, now = new Date()) {
  if (!deadlineDay || !deadlineDay.isValid()) return null;
  const todayKey = dayjs(now).format("YYYY-MM-DD");
  const cancelKey = deadlineDay.format("YYYY-MM-DD");
  const today = dayjs(todayKey, "YYYY-MM-DD", true);
  const cancel = dayjs(cancelKey, "YYYY-MM-DD", true);
  if (!today.isValid() || !cancel.isValid()) return null;
  return cancel.diff(today, "day");
}

export function daysUntilHotelFreeCancel(order, now = new Date()) {
  return calendarDaysUntil(parseHotelFreeCancelCalendarDay(order), now);
}

function severityForDaysLeft(daysLeft) {
  if (daysLeft === 0) return HOTEL_CXL_DROP_RISK_SEVERITY.critical;
  if (daysLeft <= 3) return HOTEL_CXL_DROP_RISK_SEVERITY.urgent;
  return HOTEL_CXL_DROP_RISK_SEVERITY.warning;
}

export function hotelCxlDropRiskCountdownText(daysLeft) {
  if (daysLeft == null || !Number.isFinite(daysLeft)) return "";
  if (daysLeft < 0) return "תאריך הביטול החינם חלף";
  if (daysLeft === 0) return "היום האחרון לביטול חינם";
  if (daysLeft === 1) return "נותר יום אחד לביטול חינם";
  return `נותרו ${daysLeft} ימים לביטול חינם`;
}

function formatDeadlineDisplay(raw, parsed) {
  const fromParsed = parsed && parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
  if (fromParsed) return fromParsed;
  if (typeof raw === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(raw.trim())) {
    return raw.trim();
  }
  return "";
}

function agentLabel(order) {
  const nameObj = order?.agentName;
  const fromObj = [nameObj?.firstName, nameObj?.lastName]
    .map((p) => strip(p))
    .filter(Boolean)
    .join(" ");
  return (
    fromObj ||
    strip(order?.agent_name) ||
    strip(order?.agentEmail) ||
    strip(order?.agent_email) ||
    strip(order?.email) ||
    ""
  );
}

function hotelLabel(order) {
  return (
    strip(order?.hotelname) ||
    strip(order?.hotelName) ||
    strip(order?.hotel_name) ||
    "מלון"
  );
}

function orderIdentity(order) {
  return strip(
    order?.id ??
      order?.order_id ??
      order?.hotelorderid ??
      order?.orderid ??
      ""
  );
}

/**
 * @returns {{
 *   atRisk: boolean,
 *   reason: string,
 *   daysLeft: number|null,
 *   severity: string|null,
 *   deadlineDisplay: string,
 *   countdownText: string,
 *   warningBody: string,
 *   hotelName: string,
 *   agentLabel: string,
 *   orderId: string,
 * }}
 */
export function resolveHotelCxlDropRisk(order, now = new Date()) {
  const empty = {
    atRisk: false,
    reason: "no_order",
    daysLeft: null,
    severity: null,
    deadlineDisplay: "",
    countdownText: "",
    warningBody:
      "ההזמנה לא שולמה. בלי תשלום היא תיפול ולא ניתן לשחזר.",
    hotelName: "",
    agentLabel: "",
    orderId: "",
  };
  if (!order || typeof order !== "object") return empty;

  const base = {
    ...empty,
    hotelName: hotelLabel(order),
    agentLabel: agentLabel(order),
    orderId: orderIdentity(order),
  };

  if (isHotelOffer(order)) return { ...base, reason: "offer" };
  if (isHotelCancelled(order)) return { ...base, reason: "cancelled" };
  if (isHotelPaid(order)) return { ...base, reason: "paid" };
  if (!isGuaranteedHotelHold(order)) return { ...base, reason: "not_guaranteed" };

  const rawDeadline = pickHotelRefundableUntilRaw(order);
  const cancelDay = parseHotelFreeCancelCalendarDay(order);
  if (!cancelDay) {
    return { ...base, reason: "no_deadline" };
  }

  const daysLeft = calendarDaysUntil(cancelDay, now);
  const deadlineDisplay = formatDeadlineDisplay(rawDeadline, cancelDay);
  const countdownText = hotelCxlDropRiskCountdownText(daysLeft);

  if (daysLeft == null) return { ...base, reason: "no_deadline", deadlineDisplay };
  if (daysLeft < 0) {
    return {
      ...base,
      reason: "deadline_passed",
      daysLeft,
      deadlineDisplay,
      countdownText,
    };
  }
  if (daysLeft > HOTEL_CXL_DROP_RISK_WINDOW_DAYS) {
    return {
      ...base,
      reason: "outside_window",
      daysLeft,
      deadlineDisplay,
      countdownText,
    };
  }

  return {
    ...base,
    atRisk: true,
    reason: "in_window",
    daysLeft,
    severity: severityForDaysLeft(daysLeft),
    deadlineDisplay,
    countdownText,
    warningBody:
      "ההזמנה משוריינת ולא שולמה. בלי תשלום עד ביטול החינם היא תיפול ולא ניתן לשחזר.",
  };
}

export function isHotelCxlDropRisk(order, now = new Date()) {
  return resolveHotelCxlDropRisk(order, now).atRisk;
}

export function collectHotelCxlDropRiskAlerts(orders, now = new Date()) {
  const list = Array.isArray(orders) ? orders : [];
  const alerts = [];
  for (const order of list) {
    const risk = resolveHotelCxlDropRisk(order, now);
    if (!risk.atRisk) continue;
    alerts.push({ order, risk });
  }
  alerts.sort((a, b) => {
    const da = a.risk.daysLeft ?? 99;
    const db = b.risk.daysLeft ?? 99;
    if (da !== db) return da - db;
    return String(a.risk.hotelName).localeCompare(String(b.risk.hotelName), "he");
  });
  return alerts;
}

export function summarizeHotelCxlDropRiskAlerts(alerts) {
  const list = Array.isArray(alerts) ? alerts : [];
  const agents = new Set();
  let minDaysLeft = null;
  let todayCount = 0;
  let urgentCount = 0;
  for (const item of list) {
    const days = item?.risk?.daysLeft;
    const agent = item?.risk?.agentLabel || item?.order?.email || "";
    if (agent) agents.add(String(agent).toLowerCase());
    if (Number.isFinite(days)) {
      if (minDaysLeft == null || days < minDaysLeft) minDaysLeft = days;
      if (days === 0) todayCount += 1;
      if (days <= 3) urgentCount += 1;
    }
  }
  return {
    count: list.length,
    agentCount: agents.size,
    minDaysLeft,
    todayCount,
    urgentCount,
  };
}

export function buildHotelCxlDropRiskOrdersPath() {
  const sp = new URLSearchParams();
  sp.set(HOTEL_CXL_DROP_RISK_URL_PARAM, "1");
  sp.set("orderStatus", "pending");
  return `${ordersDashboardCategoryPath("hotels")}?${sp.toString()}`;
}

/**
 * Remaining-days lead for the dashboard / orders-list banner.
 * Uses the soonest at-risk hotel (`minDaysLeft`), not the 15-day window cap.
 */
export function hotelCxlDropRiskDashboardDaysLead(summary) {
  const count = Number(summary?.count) || 0;
  const minDays = summary?.minDaysLeft;
  if (!Number.isFinite(minDays)) {
    return `ביטול חינם נגמר בתוך עד ${HOTEL_CXL_DROP_RISK_WINDOW_DAYS} ימים`;
  }
  if (count > 1 && minDays > 1) {
    return `נותרו עד ${minDays} ימים לביטול חינם`;
  }
  return hotelCxlDropRiskCountdownText(minDays);
}

export function buildHotelCxlDropRiskDashboardCopy(summary, { viewerIsOps = false } = {}) {
  const count = Number(summary?.count) || 0;
  if (count <= 0) return { title: "", body: "" };
  const minDays = summary?.minDaysLeft;
  const todayBit =
    Number.isFinite(minDays) && minDays === 0
      ? ""
      : summary?.todayCount > 0
        ? summary.todayCount === 1
          ? " כולל הזמנה שהיום האחרון לביטול חינם."
          : ` כולל ${summary.todayCount} הזמנות שהיום האחרון לביטול חינם.`
        : "";
  const daysLead = hotelCxlDropRiskDashboardDaysLead(summary);
  if (viewerIsOps) {
    const agents = Number(summary?.agentCount) || 0;
    const agentsBit =
      agents > 1 ? ` אצל ${agents} סוכנים` : agents === 1 ? " אצל סוכן אחד" : "";
    return {
      title: `${count} מלונות משוריינים לא משולמים${agentsBit}`,
      body: `${daysLead}. בלי תשלום ההזמנות ייפלו ולא ניתן לשחזר.${todayBit}`,
    };
  }
  return {
    title:
      count === 1
        ? "יש לך מלון משוריין שלא שולם"
        : `יש לך ${count} מלונות משוריינים שלא שולמו`,
    body: `${daysLead}. בלי תשלום ההזמנה תיפול ולא ניתן לשחזר.${todayBit}`,
  };
}
